import dayjs, { Dayjs } from 'dayjs';
import { useMemo } from 'react';

import type { LocationHistoryMinimal } from '~hooks/useLocationHistory/models';
type LoadSegment = {
  start: Dayjs;
  end: Dayjs;
  id: string;
};

const useGetLoadSegments = (pings: LocationHistoryMinimal[]) =>
  useMemo(() => {
    // Group pings by loadId
    const loadGroups = pings.reduce(
      (groups, ping) => {
        if (!ping.loadId) return groups;

        if (!groups[ping.loadId]) {
          groups[ping.loadId] = [];
        }
        groups[ping.loadId].push(ping);
        return groups;
      },
      {} as Record<string, LocationHistoryMinimal[]>,
    );

    // Create segments from first and last ping of each load
    const formattedSegments: LoadSegment[] = Object.values(loadGroups)
      .filter((segment) => segment.length > 1)
      .map((segment) => ({
        start: segment[0].occurredAt,
        end: segment[segment.length - 1].occurredAt,
        id: segment[0].loadId || '',
      }))
      .sort((a, b) => dayjs(a.start).valueOf() - dayjs(b.start).valueOf()) // Sort by start time
      .map((segment, index, array) => {
        if (index === 0) return segment;
        // Use the end of previous segment as the start of current segment
        return {
          start: array[index - 1].end,
          end: segment.end,
          id: segment.id,
        };
      });

    return formattedSegments;
  }, [pings.length]);

export { useGetLoadSegments };
